/**
 * Word co-occurrence graph for two-word phrase analysis
 * Ported from words_cloud_model.coffee WordGraph class
 */

class WordGraphNode {
  constructor(name) {
    this.name = name
    this.join = {}
  }
}

/**
 * Graph structure for word co-occurrence analysis
 * Used for building two-word phrases from sentences
 */
export class WordGraph {
  constructor() {
    this.nodes = {}
  }

  /**
   * Get or create a node
   * @param {string} name - Word name
   * @returns {WordGraphNode}
   */
  node(name) {
    if (!this.nodes[name]) {
      this.nodes[name] = new WordGraphNode(name)
    }
    return this.nodes[name]
  }

  /**
   * Check if a node exists
   * @param {string} name - Word name
   * @returns {boolean}
   */
  isNode(name) {
    return Boolean(this.nodes[name])
  }

  /**
   * Connect two words with a weight
   * @param {string} from - First word
   * @param {string} to - Second word
   * @param {number} weight - Connection weight
   */
  connect(from, to, weight) {
    if (!from || !to) return

    const fromNode = this.node(from)
    const toNode = this.node(to)

    // Initialize join if needed
    if (!fromNode.join[to]) {
      fromNode.join[to] = { weight: 0, times: 0, d12: 0, d21: 0 }
    }
    if (!toNode.join[from]) {
      toNode.join[from] = { weight: 0, times: 0, d12: 0, d21: 0 }
    }

    // Update weights
    fromNode.join[to].weight += weight
    fromNode.join[to].times += 1
    fromNode.join[to].d12 += weight

    toNode.join[from].weight += weight
    toNode.join[from].times += 1
    toNode.join[from].d21 += weight
  }

  /**
   * Get weight between two words
   * @param {string} from - First word
   * @param {string} to - Second word (optional)
   * @returns {Object|null} Weight info { weight, times, d12, d21 }
   */
  weight(from, to) {
    const fromNode = this.nodes[from]
    if (!fromNode) return null

    if (to) {
      return fromNode.join[to] || null
    }

    // Return total weight for node
    let totalWeight = 0
    let totalTimes = 0
    for (const word in fromNode.join) {
      totalWeight += fromNode.join[word].weight
      totalTimes += fromNode.join[word].times
    }
    return { weight: totalWeight, times: totalTimes }
  }

  /**
   * Merge one node into another (for spell correction, case normalization)
   * @param {string} from - Node to merge from (will be removed)
   * @param {string} to - Node to merge into
   */
  merge(from, to) {
    if (from === to || !this.nodes[from]) return

    const fromNode = this.nodes[from]
    const toNode = this.node(to)

    // Transfer all connections
    for (const word in fromNode.join) {
      if (word === to) continue

      const conn = fromNode.join[word]

      if (!toNode.join[word]) {
        toNode.join[word] = { weight: 0, times: 0, d12: 0, d21: 0 }
      }

      toNode.join[word].weight += conn.weight
      toNode.join[word].times += conn.times
      toNode.join[word].d12 += conn.d12
      toNode.join[word].d21 += conn.d21

      // Update reverse connection
      if (this.nodes[word] && this.nodes[word].join[from]) {
        const reverseConn = this.nodes[word].join[from]

        if (!this.nodes[word].join[to]) {
          this.nodes[word].join[to] = { weight: 0, times: 0, d12: 0, d21: 0 }
        }

        this.nodes[word].join[to].weight += reverseConn.weight
        this.nodes[word].join[to].times += reverseConn.times
        this.nodes[word].join[to].d12 += reverseConn.d12
        this.nodes[word].join[to].d21 += reverseConn.d21

        delete this.nodes[word].join[from]
      }
    }

    // Remove the merged node
    delete this.nodes[from]
  }

  /**
   * Get top weighted two-word phrases
   * @param {number} limit - Max number of phrases to return
   * @returns {Array<{text: string, weight: number, times: number}>}
   */
  topWeights(limit = 100) {
    const phrases = []
    const seen = new Set()

    for (const word1 in this.nodes) {
      const node = this.nodes[word1]

      for (const word2 in node.join) {
        const key = [word1, word2].sort().join('|')
        if (seen.has(key)) continue
        seen.add(key)

        const conn = node.join[word2]
        const text = conn.d12 > conn.d21 ? `${word1} ${word2}` : `${word2} ${word1}`

        phrases.push({
          text,
          weight: conn.weight,
          times: conn.times
        })
      }
    }

    return phrases.sort((a, b) => b.weight - a.weight).slice(0, limit)
  }
}

/**
 * Build word graph from sentences
 * @param {number[][]} sentences - Array of word index arrays
 * @param {Array} words - Words data array
 * @param {Function} isValidWord - Filter function for words
 * @returns {WordGraph}
 */
export function calcWordGraph(sentences, words, isValidWord) {
  const graph = new WordGraph()

  for (const sentence of sentences) {
    const validWords = []

    for (const index of sentence) {
      if (isValidWord(words[index])) {
        validWords.push(words[index][0])
      }
    }

    const len = validWords.length
    for (let i = 0; i < len; i++) {
      // Adjacent words (weight 2)
      if (i + 1 < len) {
        graph.connect(validWords[i], validWords[i + 1], 2)
      }
      // Words with one gap (weight 0.5)
      if (i + 2 < len) {
        graph.connect(validWords[i], validWords[i + 2], 0.5)
      }
    }
  }

  return graph
}

export default {
  WordGraph,
  calcWordGraph
}
